import { useEffect, useReducer, useRef, useState } from 'react';
import { AudioLines, Check, ChevronRight, CircleHelp, Info, Mic, Minus, Plus, SkipForward, Volume2, X } from 'lucide-react';

import { loadStep } from '../../utils/guidedSession';
import { trackEvent } from '../../utils/gt';
import { prefersReducedMotion } from '../../utils/motion';

import { HERO_WORKOUT, THUMBS_URL, clock, formatNumber, unitFor, useLanding } from './landingData';
import { PhoneFrame } from './PhoneFrame';
import { ExerciseArt, Ring } from './parts';
import { nextSet, sessionStep, setsDone, startSession, totalSets } from './sessionState';

const TOTAL_SETS = totalSets(HERO_WORKOUT);

/**
 * The hero's phone, working: finish a set and the rest counts down (±15 s, skip), then the next set comes up; "Como
 * executar" opens the exercise's real steps. Beside it, the same moment as the Alexa skill says it (pt: the skill's own
 * lines, from the Alexa page; en: only the start phrase the site already uses, since the voice guide is pt only).
 */
export const SessionDemo = () => {
  const { t, language, name } = useLanding();
  const unit = unitFor(language);
  const s = (key, options) => t(`pages.WorkoutExecution.session.${key}`, options);
  const [state, dispatch] = useReducer((current, action) => sessionStep(current, action, HERO_WORKOUT, unit), unit, (u) =>
    startSession(HERO_WORKOUT, u)
  );
  const [howOpen, setHowOpen] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const rootRef = useRef(null);
  const howOpenerRef = useRef(null);
  const focusWasInside = useRef(false);
  const loadMoreRef = useRef(null);
  const doneRef = useRef(null);
  // The hint that the phone works (a finger shows it, then "Sua vez"): it goes at the first touch or key inside it
  const [hasPlayed, setHasPlayed] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [tap, setTap] = useState(null);
  const markPlayed = () => {
    if (hasPlayed) return;
    setHasPlayed(true);
    trackEvent('hero_demo_play', 'Landing Hero - Play Demo', 'landing_hero', true);
  };

  const exercise = HERO_WORKOUT[state.ex];
  const next = nextSet(state, HERO_WORKOUT);
  const isRest = state.view === 'rest';
  const loadText = (value) => `${formatNumber(value, language)} ${unit}`;
  const step = loadStep(exercise.equipment, exercise.en, unit);
  // The how-to of what's on screen: during the rest, the set that comes next
  const howExercise = isRest ? next.exercise : exercise;

  // The rest counts down a second at a time; it ends on its own
  useEffect(() => {
    if (!isRest) return undefined;
    const timer = setInterval(() => dispatch({ type: 'tick' }), 1000);
    return () => clearInterval(timer);
  }, [isRest, state.ex, state.set]);

  // The hint starts once most of the phone is on screen (on phones it sits under the buttons)
  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') {
      setIsInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => entry.isIntersecting && setIsInView(true), { threshold: 0.6 });
    observer.observe(root);
    return () => observer.disconnect();
  }, []);

  // A finger shows the phone working (one more on the load, then Concluir) and hands it over with "Sua vez"; with
  // reduced motion, only the hand-over
  useEffect(() => {
    if (hasPlayed || !isInView) return undefined;
    if (prefersReducedMotion()) {
      setTap({ phase: 'yourTurn' });
      return undefined;
    }
    // A button's center inside the phone's app, in its own 390px units (the frame zooms the app)
    const at = (element) => {
      if (!element) return null;
      let x = element.offsetWidth / 2;
      let y = element.offsetHeight / 2;
      for (let node = element; node && node !== rootRef.current; node = node.offsetParent) {
        x += node.offsetLeft;
        y += node.offsetTop;
      }
      return { x, y };
    };
    const press = (pressed) => () => setTap((current) => ({ ...current, pressed }));
    const steps = [
      [700, () => at(loadMoreRef.current) && setTap({ ...at(loadMoreRef.current), phase: 'show' })],
      [1500, press(true)],
      [1650, () => {
        dispatch({ type: 'load', delta: 1 });
        setTap((current) => ({ ...current, pressed: false }));
      }],
      [2300, () => at(doneRef.current) && setTap({ ...at(doneRef.current), phase: 'show' })],
      [3200, press(true)],
      [3400, press(false)],
      [3900, () => setTap({ phase: 'yourTurn' })],
    ];
    const timers = steps.map(([ms, step]) => setTimeout(step, ms));
    return () => timers.forEach(clearTimeout);
  }, [hasPlayed, isInView]);

  // Where the focus last landed: a control that disappears with its screen hands the focus to the new screen's action
  useEffect(() => {
    const track = (event) => {
      focusWasInside.current = !!rootRef.current?.contains(event.target);
    };
    document.addEventListener('focusin', track);
    return () => document.removeEventListener('focusin', track);
  }, []);

  // Each new screen (rest, next set): one line for screen readers, and the focus on its action if it was in here
  const screenKey = `${state.view}-${state.ex}-${state.set}`;
  const firstScreen = useRef(screenKey);
  useEffect(() => {
    if (firstScreen.current === screenKey) return;
    firstScreen.current = null;
    setAnnouncement(
      isRest
        ? t('pages.Home.demo.announceRest', { n: state.set + 1, time: clock(state.total) })
        : t('pages.Home.demo.announceSet', {
            exercise: name(exercise),
            n: state.set + 1,
            total: exercise.sets,
            reps: state.reps,
            load: loadText(state.load),
          })
    );
    const active = document.activeElement;
    if (focusWasInside.current && (!active || active === document.body)) {
      rootRef.current?.querySelector('[data-primary]')?.focus({ preventScroll: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenKey]);

  const openHow = (event) => {
    howOpenerRef.current = event.currentTarget;
    setHowOpen(true);
  };
  const closeHow = () => {
    setHowOpen(false);
    howOpenerRef.current?.focus({ preventScroll: true });
  };

  // ── The talk beside the phone ──
  let talk;
  if (language === 'pt') {
    const tk = (key, options) => t(`pages.Tutorial.talk.${key}`, options);
    const said = (n) => (n === 1 ? tk('one') : n === 2 ? tk('two') : n);
    const reps = (n) => tk('reps', { count: n, n: said(n) });
    const sets = (n) => tk('sets', { count: n, n: said(n) });
    const time = (seconds) => {
      if (seconds < 60) return tk('seconds', { count: seconds });
      const minutes = tk('minutes', { count: Math.floor(seconds / 60) });
      return seconds % 60 ? tk('and', { a: minutes, b: tk('seconds', { count: seconds % 60 }) }) : minutes;
    };
    const alexaSays =
      state.set === 0
        ? tk('lines.nextExercise', {
            exercise: name(exercise),
            sets: sets(exercise.sets),
            reps: reps(exercise.reps),
            load: ` ${tk('load', { weight: formatNumber(exercise.load.kg, 'pt'), unit: 'kg' })}`,
          })
        : tk('lines.nextSet', { n: state.set + 1, exercise: name(exercise), reps: reps(exercise.reps) });
    const youSay = tk('lines.rest');
    talk = isRest
      ? [
          { who: 'you', text: youSay },
          { who: 'alexa', text: tk('lines.restReply', { time: time(state.total) }) },
        ]
      : [
          { who: 'alexa', text: alexaSays },
          { who: 'you', text: youSay },
        ];
  } else {
    talk = [{ who: 'you', text: t('pages.Home.hero.voiceStart') }];
  }

  const bars = HERO_WORKOUT.map((item, index) => {
    let done = 0;
    if (index < state.ex) done = item.sets;
    else if (index === state.ex) done = state.set + (isRest ? 1 : 0);
    return (
      <i key={item.id} style={{ flex: item.sets }}>
        <b style={{ width: `${(done / item.sets) * 100}%` }} />
      </i>
    );
  });

  const nextDose = `${next.exercise.sets} × ${next.exercise.reps} · ${loadText(next.exercise.load[unit])}`;
  const sameExercise = next.ex === state.ex;

  return (
    <>
      <PhoneFrame className="lp-hero__phone">
        <div className="lp-g" ref={rootRef} onPointerDownCapture={markPlayed} onKeyDownCapture={markPlayed} onClickCapture={markPlayed}>
          <div className="lp-g__screen" inert={howOpen ? '' : undefined}>
            <div className="lp-g__top">
              <span className="lp-g__ic" aria-hidden="true">
                <X />
              </span>
              <p className="lp-g__title">
                <b>{t('pages.Home.demo.workout')}</b>
                <span>{s('progress', { done: setsDone(state, HERO_WORKOUT), total: TOTAL_SETS })}</span>
              </p>
              <button type="button" className="lp-g__ic" aria-label={s('howToFor', { name: name(howExercise) })} onClick={openHow}>
                <CircleHelp />
              </button>
              <span className="lp-g__ic" aria-hidden="true">
                <Volume2 />
              </span>
            </div>
            <div className="lp-g__bars" aria-hidden="true">
              {bars}
            </div>

            {isRest ? (
              <>
                <div className="lp-r" role="timer" aria-label={s('restFor', { time: clock(state.left) })}>
                  <Ring share={state.total ? state.left / state.total : 0} size={286} stroke={15}>
                    <span className="lp-r__t" aria-hidden="true">
                      <b>{clock(state.left)}</b>
                      <span>{s('rest')}</span>
                    </span>
                  </Ring>
                </div>
                <div className="lp-r__adj">
                  <button type="button" aria-label={s('minus15')} onClick={() => dispatch({ type: 'shift', seconds: -15 })}>
                    {s('minus15Short')}
                  </button>
                  <button type="button" aria-label={s('plus15')} onClick={() => dispatch({ type: 'shift', seconds: 15 })}>
                    {s('plus15Short')}
                  </button>
                </div>
                <div className="lp-r__next">
                  <ExerciseArt file={next.exercise.file} />
                  <span>
                    <b>{name(next.exercise)}</b>
                    <span>
                      {sameExercise
                        ? s('restNextSet', {
                            n: next.set + 1,
                            total: exercise.sets,
                            dose: s('setDoseLoad', { reps: state.reps, load: loadText(state.load) }),
                          })
                        : s('restNextExercise', { dose: nextDose })}
                    </span>
                  </span>
                  <ChevronRight aria-hidden="true" />
                </div>
                <div className="lp-g__foot">
                  <button type="button" className="lp-g__skip" data-primary onClick={() => dispatch({ type: 'skip' })}>
                    <SkipForward aria-hidden="true" />
                    {s('skipRest')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="lp-g__media">
                  {/* The first screen's one product image: loaded right away, the rest wait their turn */}
                  <img src={`${THUMBS_URL}${exercise.file}`} alt="" width="180" height="180" fetchpriority={state.ex === 0 ? 'high' : undefined} />
                  <button type="button" className="lp-g__how" onClick={openHow}>
                    <Info aria-hidden="true" />
                    {s('howTo')}
                  </button>
                </div>
                <p className="lp-g__name">{name(exercise)}</p>
                <p className="lp-g__meta">
                  {s('exerciseOf', { index: state.ex + 1, count: HERO_WORKOUT.length })} · {t(`exerciseFilters.muscleGroups.${exercise.muscle}`)}
                </p>
                <div className="lp-g__setrow">
                  <b>
                    {s('setNumber', { n: state.set + 1 })} <span>{s('setOfTotal', { total: exercise.sets })}</span>
                  </b>
                  <span className="lp-g__dots" aria-hidden="true">
                    {Array.from({ length: exercise.sets }, (_, index) => (
                      <i key={index} className={index < state.set ? 'is-done' : index === state.set ? 'is-now' : ''} />
                    ))}
                  </span>
                </div>
                <div className="lp-g__st" role="group" aria-label={s('setGroup', { n: state.set + 1, total: exercise.sets })}>
                  <div className="lp-st">
                    <b aria-live="polite">{state.reps}</b>
                    <span>{s('repsLabel')}</span>
                    <div className="lp-st__b">
                      <button type="button" aria-label={s('lessReps')} onClick={() => dispatch({ type: 'reps', delta: -1 })}>
                        <Minus aria-hidden="true" />
                      </button>
                      <button type="button" aria-label={s('moreReps')} onClick={() => dispatch({ type: 'reps', delta: 1 })}>
                        <Plus aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                  <div className="lp-st">
                    <b aria-live="polite">
                      {formatNumber(state.load, language)}
                      <small>{unit}</small>
                    </b>
                    <span>{s('loadLabel')}</span>
                    <div className="lp-st__b">
                      <button
                        type="button"
                        aria-label={s('lessLoad', { step: formatNumber(step, language), unit })}
                        onClick={() => dispatch({ type: 'load', delta: -1 })}
                      >
                        <Minus aria-hidden="true" />
                      </button>
                      <button
                        ref={loadMoreRef}
                        type="button"
                        aria-label={s('moreLoad', { step: formatNumber(step, language), unit })}
                        onClick={() => dispatch({ type: 'load', delta: 1 })}
                      >
                        <Plus aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="lp-g__foot">
                  <p className="lp-g__next">
                    <ChevronRight aria-hidden="true" />
                    {s('afterLabel')}{' '}
                    <b>
                      {sameExercise
                        ? s('afterSet', { n: next.set + 1, dose: s('setDoseLoad', { reps: state.reps, load: loadText(state.load) }) })
                        : name(next.exercise)}
                    </b>
                  </p>
                  <button ref={doneRef} type="button" className="lp-g__done" data-primary onClick={() => dispatch({ type: 'done' })}>
                    <Check aria-hidden="true" />
                    {s('completeSet', { n: state.set + 1 })}
                  </button>
                </div>
              </>
            )}
          </div>

          {!hasPlayed && !isRest && tap?.phase === 'yourTurn' && (
            <p className="lp-hint" aria-hidden="true">
              {t('pages.Home.hero.hintYourTurn')}
            </p>
          )}
          {!hasPlayed && tap?.phase === 'show' && (
            <span className={`lp-tap${tap.pressed ? ' is-pressed' : ''}`} style={{ left: tap.x, top: tap.y }} aria-hidden="true" />
          )}

          {howOpen && (
            <>
              <div className="lp-g__scrim" onClick={closeHow} aria-hidden="true" />
              <HowSheet exercise={howExercise} onClose={closeHow} />
            </>
          )}
          <p className="sr-only" aria-live="polite">
            {announcement}
          </p>
        </div>
      </PhoneFrame>

      <div className="lp-talk" role="group" aria-labelledby="lp-talk-caption">
        <p id="lp-talk-caption" className="lp-talk__cap">
          <Mic aria-hidden="true" />
          {t('pages.Home.hero.voiceCaption')}
        </p>
        <div className="lp-talk__lines">
          {talk.map((line) =>
            line.who === 'you' ? (
              <p key={`${line.who}-${line.text}`} className="lp-bubble lp-bubble--you">
                <span className="lp-bubble__who">{t('pages.Home.hero.you')}</span>
                {line.text}
              </p>
            ) : (
              <div key={`${line.who}-${line.text}`} className="lp-voice">
                <span className="lp-voice__disc" aria-hidden="true">
                  <AudioLines />
                </span>
                <p className="lp-bubble lp-bubble--alexa">
                  <span className="lp-bubble__who">{t('pages.Home.hero.alexa')}</span>
                  {line.text}
                </p>
              </div>
            )
          )}
        </div>
        {language !== 'pt' && <p className="lp-talk__note">{t('pages.Home.hero.voiceNote')}</p>}
      </div>
    </>
  );
};

// The exercise's real steps, as a sheet over the session (Escape or the close button)
const HowSheet = ({ exercise, onClose }) => {
  const { t, language, name } = useLanding();
  const closeRef = useRef(null);

  useEffect(() => {
    closeRef.current?.focus({ preventScroll: true });
  }, []);

  return (
    <div
      className="lp-g__sheet"
      role="dialog"
      aria-label={t('pages.WorkoutExecution.session.howToFor', { name: name(exercise) })}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="lp-g__sheet-head">
        <b>{t('pages.WorkoutExecution.session.howTo')}</b>
        <button type="button" ref={closeRef} className="lp-g__ic" aria-label={t('pages.WorkoutExecution.session.closeHowTo')} onClick={onClose}>
          <X />
        </button>
      </div>
      <ExerciseArt file={exercise.file} className="lp-g__sheet-art" />
      <p className="lp-g__sheet-name">{name(exercise)}</p>
      <ol className="lp-steps">
        {exercise.steps[language].map((text) => (
          <li key={text}>{text}</li>
        ))}
      </ol>
    </div>
  );
};
